import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";

// ─── JWT helpers ───────────────────────────────────────────────────────────────

export function decodeJwt(token) {
  if (!token) return null;
  try {
    const base64Url = token.split(".")[1] || "";
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(
      decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => `%${("00" + c.charCodeAt(0).toString(16)).slice(-2)}`)
          .join(""),
      ),
    );
  } catch {
    return null;
  }
}

function getStoredToken() {
  return localStorage.getItem("token") || null;
}

// ─── Core permission resolver ──────────────────────────────────────────────────
/**
 * TWO MODES — selected automatically:
 *
 * EXPLICIT MODE  (explicit_permissions array is non-empty)
 *   Only the permissions listed in explicit_permissions are active.
 *   allowed:1  → granted
 *   allowed:0  → denied
 *   Anything NOT present in explicit_permissions is DENIED — even if a
 *   group would have granted it.  Group permissions are completely ignored.
 *
 * GROUP MODE  (explicit_permissions is empty or absent)
 *   Group permissions are merged permissively across all groups:
 *   if ANY group grants a code/key it is granted.
 *   The legacy flat `permissions` array (same priority as group) is also
 *   included here.
 *
 * Both the short permission code (e.g. "PO_CREATE") and the
 * module:action composite key (e.g. "PURCHASE_ORDER:CREATE") are indexed
 * so that both can() call styles work interchangeably.
 */
function resolvePermissions(payload) {
  const codeMap = new Map(); // code -> boolean
  const moduleActionMap = new Map(); // "MODULE:ACTION" -> boolean

  if (!payload) return { allowedCodes: new Set(), allowedModuleActions: new Set() };

  function upsert(map, key, granted, isExplicit) {
    if (isExplicit) {
      // Explicit user-level permission ALWAYS wins — this is how we
      // "take away" or "give back" one specific permission for one
      // specific user, without touching anything else the group grants.
      map.set(key, granted);
    } else {
      // Group / legacy flat permissions: permissive merge — if ANY
      // group grants it, it's granted.
      if (!map.has(key) || granted) map.set(key, granted);
    }
  }

  function applyPerm(perm, isExplicit) {
    const granted = perm.allowed === 1 || perm.allowed === true;
    if (perm.code) upsert(codeMap, perm.code, granted, isExplicit);
    if (perm.module && perm.action) {
      const key = `${perm.module.toUpperCase()}:${perm.action.toUpperCase()}`;
      upsert(moduleActionMap, key, granted, isExplicit);
    }
  }

  // Step 1 — group-level permissions (base, lower priority)
  for (const group of payload.groups ?? []) {
    for (const perm of group.permissions ?? []) applyPerm(perm, false);
  }

  // Step 1b — legacy flat permissions array (same priority as group)
  for (const perm of payload.permissions ?? []) applyPerm(perm, false);

  // Step 2 — explicit user-level permissions (HIGHEST priority)
  // Only overrides the codes/keys it actually mentions.
  for (const perm of payload.explicit_permissions ?? []) applyPerm(perm, true);

  const allowedCodes = new Set();
  const allowedModuleActions = new Set();
  for (const [code, granted] of codeMap) if (granted) allowedCodes.add(code);
  for (const [key, granted] of moduleActionMap) if (granted) allowedModuleActions.add(key);

  return { allowedCodes, allowedModuleActions };
}

// ─── Page → required READ modules ─────────────────────────────────────────────
export const PAGE_MODULE_MAP = {
  dashboard: [],
  orders: ["PURCHASE_ORDER"],
  CreateOrder: ["PURCHASE_ORDER"],
  collection: ["COLLECTION"],
  Createcollections: ["COLLECTION"],
  "Inventory 1": ["ASSET"],
  Inventory: ["ASSET"],
  Branch: ["BRANCH"],
  branches: ["BRANCH"],
  warehouses: ["WAREHOUSE"],
  deliveries: ["TRANSIT"],
  reporting: ["ASSET"],
  Maintenance: ["USER"],
  users: ["USER"],
  "account-settings": [],
};

// ─── Context ───────────────────────────────────────────────────────────────────

const PermissionsContext = createContext(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

export function PermissionsProvider({ children }) {
  const [permSets, setPermSets] = useState(null);
  const [userPayload, setPayload] = useState(null);

  const reload = useCallback(() => {
    const token = getStoredToken();
    const payload = decodeJwt(token);
    setPayload(payload);
    setPermSets(
      payload
        ? resolvePermissions(payload)
        : { allowedCodes: new Set(), allowedModuleActions: new Set() },
    );
  }, []);

  useEffect(() => {
    reload();
    const onStorage = (e) => {
      if (e.key === "token") reload();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [reload]);

  // ── can() ─────────────────────────────────────────────────────────────────
  const can = useCallback(
    (codeOrModule, action) => {
      if (!permSets) return false;
      if (action !== undefined) {
        return permSets.allowedModuleActions.has(
          `${codeOrModule.toUpperCase()}:${action.toUpperCase()}`,
        );
      }
      return permSets.allowedCodes.has(codeOrModule);
    },
    [permSets],
  );

  // True if ANY of the supplied checks pass
  const canAny = useCallback(
    (checks) => {
      if (!permSets || !Array.isArray(checks)) return false;
      return checks.some((c) => {
        if (Array.isArray(c)) return can(c[0], c[1]);
        if (c.includes(":")) {
          const [m, a] = c.split(":");
          return can(m, a);
        }
        return can(c);
      });
    },
    [can, permSets],
  );

  // True only if ALL of the supplied checks pass
  const canAll = useCallback(
    (checks) => {
      if (!permSets || !Array.isArray(checks)) return false;
      return checks.every((c) => {
        if (Array.isArray(c)) return can(c[0], c[1]);
        if (c.includes(":")) {
          const [m, a] = c.split(":");
          return can(m, a);
        }
        return can(c);
      });
    },
    [can, permSets],
  );

  // True if the sidebar page should be rendered for this user
  const canSeePage = useCallback(
    (pageId) => {
      if (!permSets) return true; // still loading — don't block
      const modules = PAGE_MODULE_MAP[pageId];
      if (!modules || modules.length === 0) return true;
      return modules.some((mod) => can(mod, "READ"));
    },
    [can, permSets],
  );

  // ── Pre-built convenience flags ───────────────────────────────────────────
  const flags = useMemo(() => {
    if (!permSets) return {};
    return {
      // ── Purchase Orders ──────────────────────────────────────────────────
      canViewOrders: can("PURCHASE_ORDER", "READ"),
      canCreateOrders: can("PURCHASE_ORDER", "CREATE"),
      canCancelOrders: can("PURCHASE_ORDER", "CANCEL"),
      canProcessOrders: can("PURCHASE_ORDER", "PROCESS"),

      // ── Quotations ───────────────────────────────────────────────────────
      canViewQuotations: can("QUOTATION", "READ"),
      canCreateQuotations: can("QUOTATION", "CREATE"),
      canUpdateQuotations: can("QUOTATION", "UPDATE"),
      canDeleteQuotations: can("QUOTATION", "DELETE"),

      // ── Invoices ─────────────────────────────────────────────────────────
      canViewInvoices: can("INVOICE", "READ"),
      canCreateInvoices: can("INVOICE", "CREATE"),
      canUpdateInvoices: can("INVOICE", "UPDATE"),
      canDeleteInvoices: can("INVOICE", "DELETE"),

      // ── Collections ──────────────────────────────────────────────────────
      canViewCollections: can("COLLECTION", "READ"),
      canCreateCollections: can("COLLECTION", "CREATE"),
      canUpdateCollections: can("COLLECTION", "UPDATE"),
      canDeleteCollections: can("COLLECTION", "DELETE"),

      // ── Assets ───────────────────────────────────────────────────────────
      canViewAssets: can("ASSET", "READ"),
      canCreateAssets: can("ASSET", "CREATE"),
      canUpdateAssets: can("ASSET", "UPDATE"),
      canDeleteAssets: can("ASSET", "DELETE"),

      // ── Branches ─────────────────────────────────────────────────────────
      canViewBranches: can("BRANCH", "READ"),
      canCreateBranches: can("BRANCH", "CREATE"),
      canUpdateBranches: can("BRANCH", "UPDATE"),
      canDeleteBranches: can("BRANCH", "DELETE"),

      // ── Warehouses ───────────────────────────────────────────────────────
      canViewWarehouses: can("WAREHOUSE", "READ"),
      canCreateWarehouses: can("WAREHOUSE", "CREATE"),
      canUpdateWarehouses: can("WAREHOUSE", "UPDATE"),
      canDeleteWarehouses: can("WAREHOUSE", "DELETE"),

      // ── Transit / Deliveries ─────────────────────────────────────────────
      canViewTransit: can("TRANSIT", "READ"),
      canCreateTransit: can("TRANSIT", "CREATE"),
      canUpdateTransit: can("TRANSIT", "UPDATE"),
      canDeleteTransit: can("TRANSIT", "DELETE"),

      // ── Users ────────────────────────────────────────────────────────────
      canViewUsers: can("USER", "READ"),
      canCreateUsers: can("USER", "CREATE"),
      canUpdateUsers: can("USER", "UPDATE"),
      canDeleteUsers: can("USER", "DELETE"),

      // ── Groups ───────────────────────────────────────────────────────────
      canViewGroups: can("GROUP", "READ"),
      canCreateGroups: can("GROUP", "CREATE"),
      canUpdateGroups: can("GROUP", "UPDATE"),
      canDeleteGroups: can("GROUP", "DELETE"),

      // ── User Permissions (module: PERMISSION) ────────────────────────────
      canViewPermissions: can("PERMISSION", "READ"),
      canCreatePermissions: can("PERMISSION", "CREATE"),
      canUpdatePermissions: can("PERMISSION", "UPDATE"),
      canDeletePermissions: can("PERMISSION", "DELETE"),

      // ── Access-group permission maintenance (module: ACCESS_GROUP) ────────
      canUpdateAccessGroup: can("ACCESS_GROUP", "UPDATE"),

      // ── User–group assignment maintenance (module: USER_GROUP) ────────────
      canUpdateUserGroup: can("USER_GROUP", "UPDATE"),

      // ── Reporting (no dedicated module — gate on GROUP READ for now) ──────
      canViewReports: can("GROUP", "READ"),
    };
  }, [can, permSets]);

  const value = useMemo(
    () => ({
      /** true once the token has been decoded and permission sets are built */
      ready: permSets !== null,

      /** Raw decoded JWT payload — use sparingly */
      userPayload,

      /**
       * Core check function — two styles:
       *   can("PO_CREATE")                 short-code lookup
       *   can("PURCHASE_ORDER", "CREATE")  module + action lookup
       */
      can,

      /** True if ANY of the supplied checks pass */
      canAny,

      /** True only if ALL of the supplied checks pass */
      canAll,

      /** True if the sidebar page should be rendered for this user */
      canSeePage,

      /** Pre-built convenience flags — see flags object above */
      ...flags,

      /** Re-read the token and rebuild sets (call after login / token refresh) */
      reload,

      /** Raw resolved sets — for advanced / diagnostic use only */
      _permSets: permSets,
    }),
    [permSets, userPayload, can, canAny, canAll, canSeePage, flags, reload],
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

// ─── Hook ──────────────────────────────────────────────────────────────────────

export function usePermissions() {
  const ctx = useContext(PermissionsContext);
  if (!ctx) {
    throw new Error(
      "usePermissions must be used within a <PermissionsProvider>",
    );
  }
  return ctx;
}

// ─── HOC (for class components if ever needed) ────────────────────────────────

export function withPermissions(Component) {
  return function WrappedWithPermissions(props) {
    const permissions = usePermissions();
    return <Component {...props} permissions={permissions} />;
  };
}

// ─── Standalone helper (for files that cannot use the hook) ───────────────────

export function standaloneCanCheck(codeOrModule, action) {
  const token = getStoredToken();
  const payload = decodeJwt(token);
  if (!payload) return false;
  const { allowedCodes, allowedModuleActions } = resolvePermissions(payload);
  if (action !== undefined) {
    return allowedModuleActions.has(
      `${codeOrModule.toUpperCase()}:${action.toUpperCase()}`,
    );
  }
  return allowedCodes.has(codeOrModule);
}

export default PermissionsContext;
